import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { callBackendApi } from "../../../methods/axiosInstances/backendInstance/apiRequest";
import { showSnackbar } from "../../../store/snackbarSlice/snackbarSlice";

const normalizeRows = (response) => {
  if (!response?.success) return [];
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const startOfDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return "-";
  return date.toLocaleDateString([], { year: "numeric", month: "2-digit", day: "2-digit" });
};

const formatAmount = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toTimestamp = (value, fallback = Number.MAX_SAFE_INTEGER) => {
  const date = parseDate(value);
  if (!date) return fallback;
  return date.getTime();
};

const sumBy = (rows, fieldName) => {
  return rows.reduce((acc, item) => acc + Number(item?.[fieldName] || 0), 0);
};

const canCollectInstallment = (item) => {
  return (
    item?.type === "installment"
    && Number(item?.outstandingAmount || 0) > 0
    && ["pending", "partial", "overdue"].includes(String(item?.status || "").toLowerCase())
    && String(item?.paymentProvider || "").toLowerCase() === "stripe"
    && String(item?.mandateStatus || "").toLowerCase() === "active"
  );
};

export default function DashboardContractPaymentsPanel() {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [metrics, setMetrics] = React.useState({
    overdueCount: 0,
    overdueAmount: 0,
    upcomingCount: 0,
    upcomingAmount: 0,
    debtCount: 0,
    debtAmount: 0,
  });
  const [overdueItems, setOverdueItems] = React.useState([]);
  const [upcomingItems, setUpcomingItems] = React.useState([]);
  const [debtItems, setDebtItems] = React.useState([]);
  const [actionLoading, setActionLoading] = React.useState({});

  const setLoadingKey = React.useCallback((key, value) => {
    setActionLoading((prev) => ({ ...prev, [key]: value }));
  }, []);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError("");

    const [contractsResponse, installmentsResponse, debtsResponse] = await Promise.all([
      callBackendApi("/contracts", "GET"),
      callBackendApi("/contract-installments", "GET"),
      callBackendApi("/contract-debts", "GET"),
    ]);

    const contracts = normalizeRows(contractsResponse);
    const installments = normalizeRows(installmentsResponse);
    const debts = normalizeRows(debtsResponse);

    if (!contractsResponse?.success || !installmentsResponse?.success || !debtsResponse?.success) {
      const errors = [
        contractsResponse?.success ? null : (contractsResponse?.error || "contracts"),
        installmentsResponse?.success ? null : (installmentsResponse?.error || "installments"),
        debtsResponse?.success ? null : (debtsResponse?.error || "debts"),
      ].filter(Boolean);
      setError(`Failed to load some payment data: ${errors.join(" | ")}`);
    }

    const contractById = Object.fromEntries(contracts.map((contract) => [contract.id, contract]));
    const installmentById = Object.fromEntries(installments.map((installment) => [installment.id, installment]));

    const today = startOfDay(new Date());
    const upcomingEnd = startOfDay(new Date());
    if (upcomingEnd) {
      upcomingEnd.setDate(upcomingEnd.getDate() + 7);
    }

    const normalizedInstallments = installments.map((installment) => {
      const contract = contractById[installment.contract_id] || {};
      return {
        type: "installment",
        id: installment.id,
        contractId: installment.contract_id,
        contractNumber: contract?.contract_number || installment?.contract?.contract_number || installment.contract_id,
        userId: contract?.user?.id || contract?.user_id || null,
        memberName: contract?.user?.name || contract?.user?.email || "Member",
        memberEmail: contract?.user?.email || "",
        dueDate: installment.due_date,
        status: installment.status,
        paymentProvider: contract?.payment_provider || installment?.contract?.payment_provider || "",
        mandateStatus: contract?.mandate_status || installment?.contract?.mandate_status || "",
        expectedAmount: Number(installment.expected_amount || 0),
        outstandingAmount: Number(installment.outstanding_amount || 0),
      };
    });

    const pendingInstallments = normalizedInstallments.filter((item) => {
      return (
        Number(item.outstandingAmount) > 0
        && !["paid", "cancelled"].includes(String(item.status || "").toLowerCase())
      );
    });

    const overdueInstallments = pendingInstallments
      .filter((item) => {
        const dueDate = startOfDay(item.dueDate);
        return dueDate && today && dueDate.getTime() < today.getTime();
      })
      .sort((left, right) => toTimestamp(left.dueDate, 0) - toTimestamp(right.dueDate, 0));

    const upcomingInstallments = pendingInstallments
      .filter((item) => {
        const dueDate = startOfDay(item.dueDate);
        return dueDate
          && today
          && upcomingEnd
          && dueDate.getTime() >= today.getTime()
          && dueDate.getTime() <= upcomingEnd.getTime();
      })
      .sort((left, right) => toTimestamp(left.dueDate) - toTimestamp(right.dueDate));

    const normalizedDebts = debts
      .map((debt) => {
        const contract = contractById[debt.contract_id] || {};
        const linkedInstallment = installmentById[debt.contract_installment_id] || {};
        return {
          type: "debt",
          id: debt.id,
          contractId: debt.contract_id,
          contractNumber: contract?.contract_number || debt?.contract?.contract_number || debt.contract_id,
          userId: contract?.user?.id || contract?.user_id || null,
          memberName: contract?.user?.name || contract?.user?.email || "Member",
          memberEmail: contract?.user?.email || "",
          dueDate: linkedInstallment?.due_date || debt.opened_at,
          status: debt.status,
          outstandingAmount: Number(debt.outstanding_amount || 0),
          totalAmount: Number(debt.total_amount || 0),
        };
      })
      .filter((item) => {
        return (
          Number(item.outstandingAmount) > 0
          && ["open", "partial"].includes(String(item.status || "").toLowerCase())
        );
      })
      .sort((left, right) => toTimestamp(left.dueDate) - toTimestamp(right.dueDate));

    setMetrics({
      overdueCount: overdueInstallments.length,
      overdueAmount: sumBy(overdueInstallments, "outstandingAmount"),
      upcomingCount: upcomingInstallments.length,
      upcomingAmount: sumBy(upcomingInstallments, "outstandingAmount"),
      debtCount: normalizedDebts.length,
      debtAmount: sumBy(normalizedDebts, "outstandingAmount"),
    });

    setOverdueItems(overdueInstallments.slice(0, 6));
    setUpcomingItems(upcomingInstallments.slice(0, 6));
    setDebtItems(normalizedDebts.slice(0, 6));
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const warnUser = React.useCallback(async (item) => {
    const contractId = item?.contractId;
    if (!contractId) {
      dispatch(showSnackbar({ message: "Cannot resolve contract id for warning." }));
      return;
    }

    const actionKey = `warn:${item.type}:${item.id}`;
    setLoadingKey(actionKey, true);
    const response = await callBackendApi(`/contracts/${contractId}/payment-warning`, "POST", {
      context_type: item.type,
      contract_installment_id: item.type === "installment" ? item.id : undefined,
      contract_debt_id: item.type === "debt" ? item.id : undefined,
    });
    setLoadingKey(actionKey, false);

    if (!response?.success) {
      dispatch(showSnackbar({ message: response?.error || "Failed to send warning." }));
      return;
    }

    dispatch(showSnackbar({ message: "Warning email sent to the member." }));
  }, [dispatch, setLoadingKey]);

  const collectInstallment = React.useCallback(async (item) => {
    if (!item?.id) return;
    const actionKey = `collect:${item.id}`;
    setLoadingKey(actionKey, true);
    const response = await callBackendApi(`/contracts/installments/${item.id}/collect`, "POST");
    setLoadingKey(actionKey, false);

    if (!response?.success) {
      dispatch(showSnackbar({ message: response?.error || "Failed to submit collection." }));
      return;
    }

    dispatch(showSnackbar({ message: "Collection submitted successfully." }));
    loadData();
  }, [dispatch, loadData, setLoadingKey]);

  const renderRows = (items, emptyText) => {
    if (!items.length) {
      return <Typography color="text.secondary">{emptyText}</Typography>;
    }

    return (
      <Stack spacing={1.25}>
        {items.map((item, index) => {
          const warnKey = `warn:${item.type}:${item.id}`;
          const collectKey = `collect:${item.id}`;
          const canCollect = canCollectInstallment(item);

          return (
            <Box key={`${item.type}-${item.id}`}>
              <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {item.contractNumber} - {item.memberName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Due: {formatDate(item.dueDate)} | Outstanding: {formatAmount(item.outstandingAmount)} EUR
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                  <Chip
                    size="small"
                    color={String(item.status || "").toLowerCase() === "overdue" ? "error" : "warning"}
                    label={item.status || "-"}
                  />
                  {item.userId ? (
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/dashboard/users/gym-user/${item.userId}`}
                    >
                      Open User
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => warnUser(item)}
                    disabled={Boolean(actionLoading[warnKey])}
                  >
                    Warn
                  </Button>
                  {canCollect ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => collectInstallment(item)}
                      disabled={Boolean(actionLoading[collectKey])}
                    >
                      Collect
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
              {index < items.length - 1 ? <Divider sx={{ mt: 1.25 }} /> : null}
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Card sx={{ borderRadius: 4 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography component="h2" variant="h6">
              Contract Payments Monitor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track overdue, upcoming and debt amounts. Warn members directly by email.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button component={RouterLink} to="/dashboard/contracts" size="small" variant="outlined">
              Open Contracts
            </Button>
            <Button size="small" variant="outlined" onClick={loadData}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {!loading && error ? (
          <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
        ) : null}

        {!loading ? (
          <>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 2 }}>
              <Box sx={{ flex: 1, p: 1.25, borderRadius: 2, bgcolor: "error.50" }}>
                <Typography variant="caption" color="text.secondary">Overdue installments</Typography>
                <Typography variant="h6">{metrics.overdueCount}</Typography>
                <Typography variant="body2">{formatAmount(metrics.overdueAmount)} EUR</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1.25, borderRadius: 2, bgcolor: "warning.50" }}>
                <Typography variant="caption" color="text.secondary">Upcoming 7 days</Typography>
                <Typography variant="h6">{metrics.upcomingCount}</Typography>
                <Typography variant="body2">{formatAmount(metrics.upcomingAmount)} EUR</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1.25, borderRadius: 2, bgcolor: "info.50" }}>
                <Typography variant="caption" color="text.secondary">Open debts</Typography>
                <Typography variant="h6">{metrics.debtCount}</Typography>
                <Typography variant="body2">{formatAmount(metrics.debtAmount)} EUR</Typography>
              </Box>
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Overdue Installments</Typography>
            {renderRows(overdueItems, "No overdue installments.")}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Upcoming Payments (next 7 days)</Typography>
            {renderRows(upcomingItems, "No upcoming installments in the next 7 days.")}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Open Debts</Typography>
            {renderRows(debtItems, "No open debts.")}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
